import React from "react";
import {Typography} from "antd";

const CandidateStatus = {
    StatusUninvited:  'Not Invited',
    StatusPendingInvite:  'Notified.  No Response Yet',
    StatusApproved:  'Interested!',
    StatusRejected:  'Not Interested',
    StatusWithdrawn:  'Withdrew Interest',
    StatusScopeOutlined: 'Has Outlined Scope',
    StatusQuoteConfirmed: 'Has Confirmed Quote',
}

const IsCandidate = WrappedClass => {
    return class IsCandidate extends WrappedClass {
        static CandidateStatus = CandidateStatus;
        /*
        |--------------------------------------------------------------------------
        | Candidate Helper
        |--------------------------------------------------------------------------
        */

        isFixedFee() {
            return this.job_candidate?.is_fixed_fee_contract
        }

        isFixedRetainer() {
            return this.job_candidate?.is_fixed_retainer_contract
        }

        isTimeMaterials() {
            return !this.isFixedFee() && !this.isFixedRetainer()
        }

        hasCandidateStatus()
        {
            return TB.isDefined(this.job_candidate?.status);
        }

        candidateStatus()
        {
            let statuses = TB.invert(laravel.job_candidate_statuses);
            if (this.job_candidate && this.job_candidate.status && statuses[this.job_candidate.status]) {
                return parseInt(statuses[this.job_candidate.status]);
            }

            return false;
        }

        matchStatus()
        {
            let statuses = TB.invert(laravel.job_candidate_statuses);
            if (this.job_candidate && this.job_candidate.client_match_status
                && statuses[this.job_candidate.client_match_status]) {
                return parseInt(statuses[this.job_candidate.client_match_status]);
            }

            return false;
        }

        matchStatusName()
        {
            return laravel.job_candidate_statuses[this.matchStatus()];
        }

        candidateStatusName()
        {
            return laravel.job_candidate_statuses[this.candidateStatus()];
        }

        hasConfirmedAvailabilityForOpportunities() {
            return this.has_confirmed_talent_availability
        }

        statusImage() {
            if (this.shortlistedAccepted()) {
                return '/images/profile-banners/shortlisted-banner.png'
            }
            if(this.declinedPosition()) {
                return '/images/profile-banners/declined-banner.png'
            }
            if (this.matchStatusIsInterested()) {
                return '/images/profile-banners/interested-banner.png'
            }
            return false;
        }
        statusLabel() {
            if (this.shortlistedAccepted()) {
                return 'Accepted'
            }
            if(this.declinedPosition()) {
                return 'Declined'
            }
            if (this.matchStatusIsInterested()) {
                return 'Interested'
            }
            return false;
        }

        hasBeenInvited() {
            return this.job_candidate?.talent_invited_at
                && (this.job_candidate?.is_shortlisted || this.matchStatusIsRejected());
        }

        matchStatusIsInterested() {
            if (this.isFixedFee() && !this.hasConfirmedScope()) {
                return false;
            }
            return this.matchStatusName() === CandidateStatus.StatusApproved
        }
        matchStatusIsUnknown() {
            return !this.matchStatus()
        }
        matchStatusIsRejected() {
            return this.matchStatusName() === CandidateStatus.StatusWithdrawn || this.matchStatusName() === CandidateStatus.StatusRejected
        }

        statusIsPending() {
            if (this.statusIsRejected() || this.statusIsWithdrawn()) {
                return false;
            }
            if (this.isFixedFee() && !this.hasConfirmedPayout() && this.hasBeenInvited()) {
                return true;
            }
            if (!this.isFixedFee() && !this.matchStatusIsInterested() && this.hasBeenInvited()) {
                return true;
            }

            return this.hasBeenInvited() && this.candidateStatusName() === CandidateStatus.StatusPendingInvite
        }

        statusIsOutlinedScope() {
            return this.candidateStatusName() === CandidateStatus.StatusScopeOutlined
        }

        statusIsQuoteConfirmed() {
            return this.candidateStatusName() === CandidateStatus.StatusQuoteConfirmed
        }
        statusIsApproved() {
            return this.candidateStatusName() === CandidateStatus.StatusApproved
        }

        statusIsRejected() {
            return this.candidateStatusName() === CandidateStatus.StatusRejected
        }

        statusIsWithdrawn() {
            return this.candidateStatusName() === CandidateStatus.StatusWithdrawn
        }

        disabledFromInvite() {
            return this.declinedPosition() || this.job_candidate?.was_disliked || this.isShortlisted()
        }

        declinedPosition() {
            return this.statusIsRejected() || this.statusIsWithdrawn()
        }

        isAssigned() {
            return this.job_candidate?.is_assigned
        }

        isShortlisted() {
            return this.job_candidate?.is_shortlisted
        }

        wasLiked() {
            return this.job_candidate?.was_liked
        }
        wasDisliked() {
            return this.job_candidate?.was_disliked
        }

        coverLetter() {
            return this.job_candidate?.cover_letter
        }
        relevantExperience() {
            return this.job_candidate?.relevant_experience
        }

        shortlistedAccepted() {
            return !this.isAssigned() && !this.declinedPosition() && this.isShortlisted() && this.hasProposalForClient();
        }

        isPendingShortlisted() {
            return !this.isAssigned() && !this.declinedPosition() && this.statusIsPending();
        }

        /*
        |--------------------------------------------------------------------------
        | Candidate Matching
        |--------------------------------------------------------------------------
        */
        hasMatching() {
            return !!this.matching
        }
        overallMatchPercent(round) {
            if (round) {
                return Math.round(this.matching?.overall?.percent)
            }
            return this.matching?.overall?.percent
        }

        /*
        |--------------------------------------------------------------------------
        | Role Match / Engagement Fit / Hire Confidence
        |--------------------------------------------------------------------------
        |
        | Role Match scores the talent's profile against the role's criteria and
        | is available as soon as they appear as a match. Engagement Fit comes
        | from the AI assessment of their submitted proposal against the role's
        | scoring criteria, so it only exists once they have proposed.
        |
        | Hire Confidence is a weighted blend of the two, plus a light
        | contribution from the talent's platform history.
        |
        */

        static HireConfidenceWeights = {
            roleMatch: 0.45,
            engagementFit: 0.45,
            marketDemand: 0.05,
            priorOutcomes: 0.05,
        };

        roleMatchScore(round = true) {
            return this.overallMatchPercent(round);
        }

        /**
         * The skills half of Role Match: how much of what the role asked for
         * the talent has on their profile. Null when the role listed no
         * skills, so the tile can drop the row rather than show a bare 0.
         */
        profileSkillsScore(round = true) {
            let percent = this.matching?.skills?.overall?.percent;
            if (!TB.isDefined(percent) || percent === null) {
                return null;
            }
            return round ? Math.round(percent) : Number(percent);
        }

        /**
         * The AI read of their resume against this role.
         *
         * Scored per-position where the backend has done so, otherwise falls
         * back to the strongest of the disciplines the talent matched on —
         * a discipline-level score is still a resume score, just a broader one.
         */
        resumeMatchScore(round = true) {
            let score = this.job_candidate?.resume_match_score;

            if (!TB.isDefined(score) || score === null) {
                // Average across the disciplines the talent matched on,
                // averaged server-side. This is also the figure the backend
                // blends into `overall.blended_percent`.
                score = this.matching?.disciplines?.resume_match_score;
            }

            if (!TB.isDefined(score) || score === null) {
                return null;
            }

            return round ? Math.round(score) : Number(score);
        }

        /**
         * Beta: an even blend of Role Match and the AI's read of the resume,
         * being trialled as the eventual replacement for Role Match.
         *
         * Null unless the resume half actually exists. The backend computes
         * `(percent + resume_match_score) / 2` without guarding the null case,
         * so a talent with no scored disciplines comes back with half their
         * match score — a plausible-looking number that means nothing.
         */
        blendedMatchScore(round = true) {
            let percent = this.matching?.overall?.blended_percent;
            let resumeScore = this.matching?.disciplines?.resume_match_score;

            if (!TB.isDefined(percent) || percent === null) {
                return null;
            }

            if (!TB.isDefined(resumeScore) || resumeScore === null) {
                return null;
            }

            return round ? Math.round(percent) : Number(percent);
        }

        engagementFitScore(round = true) {
            let score = this.job_candidate?.engagement_fit_score;
            if (!TB.isDefined(score) || score === null) {
                return null;
            }
            return round ? Math.round(score) : Number(score);
        }

        hasEngagementFit() {
            return this.engagementFitScore() !== null
                && !!this.engagementFitAssessment();
        }

        engagementFitAssessment() {
            return this.job_candidate?.engagement_fit || null;
        }

        /**
         * How often this talent gets invited and then actually selected,
         * mapped onto 0-100 with 50 as the platform average.
         *
         * Null for a talent with no history to score. That is distinct from a
         * score of 0, which is a real result and is shown as one.
         */
        hireLikelihoodScore(round = true) {
            // The transformer renamed this to `demand_score`; `hire_score` is
            // still the column name behind it but no longer reaches the client.
            return this.historyScore(this.demand_score, round);
        }

        /**
         * Prior project outcomes, already on a 0-100 scale. Null when the
         * talent has no completed work behind them.
         */
        pastPerformanceScore(round = true) {
            return this.historyScore(this.performance_score, round);
        }

        /**
         * @param {Number} score
         * @param {Boolean} round
         * @returns {Number|null}
         */
        historyScore(score, round = true) {
            if (score === null || score === undefined || score === '') {
                return null;
            }
            return round ? Math.round(score) : Number(score);
        }

        /**
         * The four scores behind Hire Confidence with the weight each carries.
         *
         * Weights are fixed — a component with no score keeps its share and
         * simply earns none of it, rather than having it spread across the
         * others. So the weights always read 45/45/5/5 and total 100%, and a
         * talent with no platform history scores at most 90.
         *
         * Returned in display order, including unscored components, so callers
         * can show what was available to earn as well as what was earned.
         *
         * @returns {Array}
         */
        hireConfidenceComponents() {
            let weights = this.constructor.HireConfidenceWeights;

            return [
                { key: 'roleMatch', label: 'Role Match', score: this.combinedRoleMatchScore(false), weight: weights.roleMatch },
                { key: 'engagementFit', label: 'Engagement Fit', score: this.engagementFitScore(false), weight: weights.engagementFit },
                { key: 'marketDemand', label: 'Market Demand', score: this.hireLikelihoodScore(false), weight: weights.marketDemand },
                { key: 'priorOutcomes', label: 'Prior Outcomes', score: this.pastPerformanceScore(false), weight: weights.priorOutcomes },
            ];
        }

        /**
         * The Role Match figure that feeds Hire Confidence: an even blend of
         * the profile-against-role match and the AI's read of the resume.
         *
         * Distinct from `roleMatchScore()`, which stays the profile-only number
         * the client-facing Match Score section shows. Falls back to that when
         * there is no resume score to blend, so a candidate is never scored on
         * half a formula.
         */
        combinedRoleMatchScore(round = true) {
            let blended = this.blendedMatchScore(round);

            return blended === null ? this.roleMatchScore(round) : blended;
        }

        /**
         * The two halves behind `combinedRoleMatchScore()`, each with the share
         * it carries of that score. For the breakdown UI.
         *
         * @returns {Array}
         */
        roleMatchComponents() {
            let hasBlend = this.blendedMatchScore() !== null;

            return [
                {
                    key: 'profileMatch',
                    label: 'Profile Match',
                    score: this.roleMatchScore(false),
                    // Without a resume score the profile half carries all of it.
                    weight: hasBlend ? 0.5 : 1,
                },
                {
                    key: 'resumeMatch',
                    label: 'AI Resume Score',
                    score: this.resumeMatchScore(false),
                    weight: hasBlend ? 0.5 : 0,
                },
            ];
        }

        /**
         * Every discipline on the platform, as id → name.
         *
         * The last resort for naming a matched discipline, and the only one
         * that works on a candidate tile: the tile's payload carries no
         * `talent_disciplines` at all, and its matching rows carry a
         * `discipline_id` but no name. Without this the tile's Role Match
         * breakdown listed every matched role as "Discipline", while the same
         * panel on the profile — where `talent_disciplines` is included —
         * named them correctly.
         *
         * Built once and cached on the class; `discipline_categories` is
         * injected at boot and doesn't change within a session.
         *
         * @returns {Object}
         */
        static disciplineNames() {
            if (this.DisciplineNameCache) {
                return this.DisciplineNameCache;
            }

            let names = {};

            TB.each(
                (typeof laravel !== 'undefined' && laravel.discipline_categories) || [],
                category => TB.each(category?.disciplines || [], discipline => {
                    if (discipline?.id && discipline?.name) {
                        names[discipline.id] = discipline.name;
                    }
                })
            );

            this.DisciplineNameCache = names;

            return names;
        }

        /**
         * Matched disciplines carrying an AI resume score, each with the
         * breakdown behind it. Ordered strongest first.
         *
         * The matching payload doesn't always name its disciplines, so the
         * name is resolved here rather than left for every caller to work out.
         *
         * @returns {Array}
         */
        scoredDisciplines() {
            // `talent_disciplines` are keyed by discipline id — the same id a
            // matching row points at with `discipline_id`, not its own row id.
            // Joining on the wrong one silently yields unnamed disciplines.
            let profiled = TB.keyBy(this.talent_disciplines || [], 'id');
            let levels = (typeof laravel !== 'undefined' && laravel.talent_experience_levels) || {};
            let names = this.constructor.disciplineNames();

            let scored = TB.filter(
                this.matching?.disciplines?.disciplines || [],
                discipline => TB.isDefined(discipline?.resume_match_score)
                    && discipline?.resume_match_score !== null
            );

            return TB.orderBy(
                TB.map(scored, discipline => {
                    let profile = profiled[discipline.discipline_id]
                        || profiled[discipline.id]
                        || {};

                    let level = levels[profile.talent_score || discipline.talent_score];

                    return {
                        ...discipline,
                        // Platform lookup last: the talent's own record is the
                        // better source where it exists, since it is what the
                        // rest of their profile is named from.
                        name: discipline.name
                            || profile.name
                            || names[discipline.discipline_id]
                            || names[discipline.id]
                            || 'Discipline',
                        experience_label: level
                            ? `${level.level} (${level.label})`
                            : null,
                    };
                }),
                ['resume_match_score'],
                ['desc']
            );
        }

        /**
         * Weighted blend of the components above. Null until the candidate has
         * an Engagement Fit assessment, which is what gates the tab.
         */
        hireConfidenceScore(round = true) {
            if (this.engagementFitScore() === null) {
                return null;
            }

            // An unscored component contributes nothing but still consumes its
            // weight — see hireConfidenceComponents().
            let score = TB.sumBy(
                this.hireConfidenceComponents(),
                component => (component.score || 0) * component.weight
            );

            return round ? Math.round(score) : score;
        }

        hireConfidenceLabel() {
            let score = this.hireConfidenceScore();
            if (score === null) {
                return null;
            }
            if (score >= 90) return 'Highly Recommended';
            if (score >= 75) return 'Recommended';
            if (score >= 60) return 'Worth Considering';
            return 'Low Confidence';
        }

        /**
         * Track record shown beside the Hire Confidence score. Null when the
         * talent has no completed projects to report.
         */
        performanceSummary() {
            let performance = this.performance;
            if (!performance || !performance.completed_projects_count) {
                return null;
            }
            return performance;
        }

        /**
         * How sought-after the talent is on the platform, shown as a badge
         * beside their track record.
         *
         * `transformed_demand()` always returns an object and only fills
         * `badge` above the "High Demand" threshold, so the badge is what
         * decides whether there is anything to show. Invites and likes below
         * that threshold aren't a signal worth surfacing on their own.
         */
        demandSummary() {
            let demand = this.demand;
            if (!demand || !demand.badge) {
                return null;
            }
            return demand;
        }

        // matchScoresForCriteria(criteria = {}) {
        //
        //
        //     console.log('matchScoresForCriteria', {metrics: this.metrics,criteria});
        //
        //
        //     let matchScores = this.metrics;
        //     if (matchScores.industries_brands_match) {
        //         matchScores.industries_brands_match.total_max_count = (matchScores.industries_brands_match.brands_max_count || 0) +
        //             (matchScores.industries_brands_match.industries_max_count || 0)
        //         matchScores.industries_brands_match.total_count = (matchScores.industries_brands_match.brands_count || 0) +
        //             (matchScores.industries_brands_match.industries_count || 0)
        //     }
        //
        //     const seniorExp = criteria.wants_senior_experience || criteria.experience?.senior;
        //     const intermediateExp = criteria.wants_intermediate_experience || criteria.experience?.intermediate;
        //     const juniorExp = criteria.wants_junior_experience || criteria.experience?.junior;
        //
        //     const hasExperienceCriteria = seniorExp || intermediateExp || juniorExp;
        //
        //     let criteriaByImportance = TB.cloneDeep(criteria);
        //     for (let criteriaType of ['industries', 'skills']) {
        //         if (criteria[criteriaType]?.length) {
        //             for(let[level, importanceLabel] of Object.entries(laravel.importance_levels)) {
        //                 criteriaByImportance[`${TB.snakeCase(importanceLabel)}_${criteriaType}`] = TB.filter(criteria[criteriaType], ['importance', parseInt(level)]);
        //                     // console.log(`matchScoresForCriteria${importanceLabel}`, criteria[criteriaType],parseInt(level),)
        //             }
        //         }
        //     }
        //     const mapCriteriaForImportance = (criteriaType, importance = 'nice_to_have', profiledItems) => {
        //
        //         importance = TB.snakeCase(importance)
        //
        //         let criteriaImportance = `${importance}_${criteriaType}`;
        //
        //         console.log('mapCriteriaForImportance', criteriaByImportance[criteriaImportance])
        //
        //         if (!profiledItems.length &&
        //             TB.isArray(criteriaByImportance[criteriaImportance]) &&
        //             !criteriaByImportance[criteriaImportance].length) return false;
        //
        //         profiledItems = TB.keyBy([...profiledItems], 'id');
        //
        //         if (TB.isArray(criteriaByImportance[criteriaImportance]) && criteriaByImportance[criteriaImportance].length) {
        //
        //             let matched = [];
        //             let unmatched = [];
        //
        //             for (let item of criteriaByImportance[criteriaImportance]) {
        //                 if (parseInt(item.id || item.value) && profiledItems[parseInt(item.id || item.value)]) {
        //                     matched.push(profiledItems[parseInt(item.id || item.value)])
        //                 } else {
        //                     unmatched.push(item);
        //                 }
        //             }
        //
        //             // console.log('mapCriteriaForImportance', matched, unmatched)
        //             matched = TB.filter([...matched], match => match);
        //             unmatched = TB.filter([...unmatched], unmatch => unmatch);
        //
        //             return {
        //                 ...(this.matching?.[criteriaType]?.[importance] || {}),
        //                 matched,
        //                 unmatched
        //             }
        //         }
        //         return false;
        //     }
        //     const matchingByProperty = (prop, profileProps = []) => {
        //         if (prop === 'skills' || prop === 'industries') {
        //             let matchScore = {};
        //
        //             let importanceLevels = TB.values(laravel.importance_levels).reverse();
        //
        //             for (let level of importanceLevels) {
        //
        //                 let matchName = TB.snakeCase(level)
        //
        //                 let match = mapCriteriaForImportance(prop, level, profileProps);
        //
        //                 console.log('matchingByProperty', match)
        //                 if (match) {
        //                     matchScore[matchName] = match
        //                 }
        //             }
        //
        //             return matchScore;
        //         }
        //         if (!criteria[prop]?.length) {
        //             return [];
        //         }
        //         let userPropsIds = TB.map(profileProps, 'id')
        //         let items = TB.map(criteria[prop], item => {
        //             let match = userPropsIds?.includes(item.id);
        //             if (match) {
        //                 return {
        //                     ...(TB.find(profileProps, [
        //                         'id',
        //                         parseInt(item.id)
        //                     ])),
        //                     match
        //                 }
        //             }
        //             return {
        //                 ...item,
        //                 match
        //             }
        //         });
        //         items = TB.orderBy(items, 'match', 'desc')
        //
        //         return items
        //
        //     }
        //
        //     const matchingExp = () => {
        //         // console.log('matchingExp', talentCriteria.disciplines);
        //
        //         const expMultiSelectMapping = {
        //             junior: 80,
        //             intermediate: 95,
        //             senior: 100,
        //         }
        //         let matched_scores = [];
        //
        //         if (seniorExp) {
        //             matched_scores.push(expMultiSelectMapping.senior)
        //         }
        //         if (intermediateExp) {
        //             matched_scores.push(expMultiSelectMapping.intermediate)
        //         }
        //         if (juniorExp) {
        //             matched_scores.push(expMultiSelectMapping.junior)
        //         }
        //
        //         let talentExpOptions = laravel.talent_experience_levels;
        //
        //         let disciplines = TB.map(criteria.disciplines || [], discipline => {
        //             let match = TB.find(TB.filter(this.talent_disciplines, dis => dis.is_approved || !TB.isDefined(dis.is_approved)), [
        //                 'id',
        //                 parseInt(discipline.id)
        //             ]);
        //
        //             let meetsCriteria = false;
        //             let selectedScore = false;
        //             let talentScore = false;
        //             if (match && match?.is_approved && match?.talent_score) {
        //                 talentScore = match.talent_score || parseInt(TB.first(TB.keys(talentExpOptions)))
        //
        //                 meetsCriteria =  matched_scores.includes(talentScore);
        //
        //                 selectedScore = talentExpOptions[talentScore];
        //             }
        //             if (!hasExperienceCriteria) {
        //                 meetsCriteria = true;
        //             }
        //             return {
        //                 match: !!match,
        //                 meets_criteria: meetsCriteria,
        //                 total_experience: talentScore,
        //                 total_experience_label: selectedScore ? `${selectedScore.level} (${selectedScore.label})` : (<Typography.Text type="secondary" className="italic">None</Typography.Text>),
        //                 ...discipline
        //             }
        //         })
        //         disciplines = TB.orderBy(disciplines, 'match', 'desc')
        //
        //         if (disciplines.length > 10) {
        //             disciplines = TB.filter(disciplines, ['match', true])
        //         }
        //         return TB.orderBy(disciplines, 'meets_criteria', 'desc')
        //     }
        //
        //     return {
        //         overall_match: matchScores.overall_match,
        //         industries_brands_match: {
        //             ...(matchScores?.industries_brands_match || {}),
        //             industries: matchingByProperty('industries', this.industries_worked || []),
        //             brands: matchingByProperty('brands', this.brands || []),
        //         },
        //         skills_match: {
        //             ...(matchScores?.skills_match || {}),
        //             skills: matchingByProperty('skills', this.skills || []),
        //         },
        //         experience_match: {
        //             ...(matchScores?.experience_match || {}),
        //             disciplines: matchingExp(),
        //             experience: {
        //                 senior: seniorExp,
        //                 intermediate: intermediateExp,
        //                 junior: juniorExp,
        //             },
        //         }
        //     }
        // }


        /*
        |--------------------------------------------------------------------------
        | Candidate Proposal
        |--------------------------------------------------------------------------
        */

        hasConfirmedScope = () => {
            return this.job_candidate?.has_confirmed_scope || this.job_candidate?.scope_confirmed_at
        }

        hasConfirmedPayout = () => {
            return this.job_candidate?.has_confirmed_payout
        }

        hasProposalForClient() {
            if (this.isFixedFee()) {
                return !this.declinedPosition() && this.hasConfirmedPayout()
            }
            let hasExistingProposal = (
                this.matchStatusIsInterested() && !this.declinedPosition()
            );
            let teamScopeOutlinedForConsultant = this.statusIsOutlinedScope() && !this.declinedPosition();

            return hasExistingProposal || this.statusIsQuoteConfirmed() || this.statusIsApproved() || teamScopeOutlinedForConsultant
        }

        hasUnseenProposalUpdate() {
            if (!this.job_candidate?.last_viewed_at || !this.job_candidate?.talent_proposal_updated_at) return true
            return TB.moment(this.job_candidate?.last_viewed_at).isBefore(this.job_candidate?.talent_proposal_updated_at)
        }

        hasSubmittedProposal() {
            return !!this.job_candidate?.talent_proposed_at
        }
        isFixedRetainerProposalValid(isRateBased = false) {
            let talentOpp = this.job_candidate?.talent_opps?.[0];

            if(!talentOpp
                || (isRateBased && (!this.job_candidate?.exact_platform_rate || !this.job_candidate?.confirmed_avg_hours_per_period))
                || (!isRateBased && !talentOpp.platform_cost)) {
                false
            }
            return true
        }
        proposedPlatformPayoutPerPeriod(isRateBased = false, formatted = true) {

            let talentOpp = this.job_candidate?.talent_opps?.[0];

            if(!this.isFixedRetainerProposalValid(isRateBased)) {
                return (
                    <i>Pending</i>
                );
            }

            console.log('proposedPlatformPayoutPerPeriod', this.job_candidate?.exact_platform_rate)
            let cost = isRateBased ? (this.job_candidate?.exact_platform_rate * this.job_candidate?.confirmed_avg_hours_per_period) :
                talentOpp.platform_cost;

            if (!formatted) {
                return parseFloat(cost);
            }
            return TB.formatCurrency(parseFloat(cost));
        }

        proposedPlatformCost(formatted = true) {

            if(!this.job_candidate?.exact_platform_cost) {
                return (
                    <i>Pending</i>
                );
            }

            let rate = this.job_candidate?.exact_platform_cost || 0
            if (!formatted) {
                return rate;
            }
            return TB.formatCurrency(rate)
        }

        proposedPlatformRate(formatted = true) {
            if (!this.job_candidate?.platform_rate) {
                return false
            }
            let rate = parseFloat(this.job_candidate?.platform_rate);

            rate = Math.ceil(rate);
            if (!formatted) {
                return rate;
            }
            return TB.formatCurrency(rate, false)
        }

        clientPreferredRate(formatted = true) {

            if (!this.client_override_hourly_rate) {
                return false
            }

            let markupMultiplier = this.markup_multiplier || (1+TB.auth().user()?.clientOrg()?.defaultPercentMarkup());

            let rate = parseFloat(this.client_override_hourly_rate * markupMultiplier);

            if (this.client_override_platform_rate) {
                rate = this.client_override_platform_rate
            }

            rate = Math.ceil(rate);
            if (!formatted) {
                return rate;
            }
            return TB.formatCurrency(rate)
        }

        standardPlatformRate(formatted = true, precise = false) {
            if (this.client_override_hourly_rate) {
                return this.clientPreferredRate(formatted)
            }
            let rate = parseFloat(this.avg_platform_rate);

            if(this.job_candidate && (this.job_candidate?.platform_standard_rate || this.job_candidate?.platform_rate)) {
                rate = parseFloat(this.job_candidate?.platform_standard_rate || 0)
                if (!this.job_candidate?.platform_standard_rate && this.job_candidate?.platform_rate) {
                    rate = parseFloat(this.job_candidate?.platform_rate)
                }
            }

            if (!precise) {
                rate = Math.ceil(rate);
            }

            if (!formatted) {
                return rate;
            }
            return TB.formatCurrency(rate)
        }

        rangedPlatformRates(showCurrencyForRange = false, formatted = true) {
            if (this.client_override_hourly_rate) {
                return this.clientPreferredRate(formatted)
            }
            let minRate = parseFloat(this.min_platform_rate);
            let maxRate = parseFloat(this.max_platform_rate);
            let avgRate = parseFloat(this.avg_platform_rate);
            if (!minRate && !maxRate && !avgRate && this.talent_disciplines?.length) {
                let rates = TB.map(this.talent_disciplines, dis => dis.standard_platform_rate ? parseFloat(dis.standard_platform_rate) : parseFloat(dis.hourly_rate || 0))
                minRate = TB.min(rates);
                maxRate = TB.max(rates);
                avgRate = TB.mean(rates);
            }

            if(maxRate && minRate && maxRate !== minRate) {
                return showCurrencyForRange ? `${TB.formatCurrency(minRate)} - ${TB.formatCurrency(maxRate)}` : `${formatted ? TB.formatCurrency(minRate) : TB.formatNumber(minRate)}-${TB.formatNumber(maxRate)}`
            }else if(!maxRate && minRate) {
                return formatted ? TB.formatCurrency(minRate) : TB.formatNumber(minRate)
            }else if(!minRate && maxRate) {
                return formatted ? TB.formatCurrency(maxRate) : TB.formatNumber(maxRate)
            }
            return formatted ? TB.formatCurrency(avgRate) : TB.formatNumber(avgRate)
        }

        changedHourlyRate() {
            return this.hasSubmittedProposal() &&
                this.proposedPlatformRate(false) &&
                this.proposedPlatformRate(false) !==
                this.standardPlatformRate(false)
        }


        availableStartDate()
        {
            // if (!this.hasSubmittedProposal()) {
            //     return 'Final Confirmation Pending';
            // }

            if (this.hasConfirmedAvailabilityForOpportunities()) {
                return 'Confirmed';
            }else {
                return 'TBD'
            }
            if (!this.hasProposalForClient() || !this.hasSubmittedProposal()) {
                return 'TBD';
            }

            if (this.confirmedStartedAt()) {
                return this.confirmedStartedAt();
            }

            return 'TBD'
        }

        confirmedStartedAt()
        {
            if (!this.job_candidate?.confirmed_started_at || !TB.moment(this.job_candidate?.confirmed_started_at).isValid()) {
                return false
            }
            if (!this.hasSubmittedProposal()) {
                return 'Final Confirmation Pending';
            }

            return TB.moment(this.job_candidate?.confirmed_started_at).format('MM/DD/YYYY');
        }

        confirmedAvailableHoursPerWeek()
        {
            if (!this.job_candidate?.confirmed_avg_hours_per_period && !parseInt(this.job_candidate?.confirmed_avg_hours_per_period)) {
                return false
            }

            if (!this.hasSubmittedProposal()) {
                return 'Final Confirmation Pending';
            }

            return `Confirmed ${this.job_candidate?.confirmed_avg_hours_per_period} Hours Per Week`;
        }

        unreadMessageCount() {
            return this.job_candidate?.unread_messages_count
        }
        hasRequestedInterview()
        {
            return TB.isDefined(this.job_candidate?.interview_requested_at);
        }

        hasInterviewUpdateSinceLastViewed(lastViewed = this.job_candidate?.last_viewed_at) {

            if(!this.job_candidate?.latest_meeting_update_at) return false;

            if(!lastViewed) return true;

            if(TB.moment(lastViewed).isSameOrBefore(this.job_candidate?.latest_meeting_update_at)) {
                return true
            }

            return TB.moment(lastViewed).diff(this.job_candidate?.latest_meeting_update_at, 'hour', true) <= 1
        }

    }
}

export default IsCandidate